import React from 'react';
import toLower from 'lodash/toLower';

import useToggle from '@lyearn/core/hooks/useToggle';
import { cn } from '@lyearn/style';
import { Box } from '@lyearn/ui';
import MissingPermissionDialog from '@/components/WorkspaceAccessControl/components/MissingPermissionDialog';
import { WorkspaceActions, WorkspaceSubjects } from '@/types/schema';

import useWorkspaceAccessControl from '../../hooks/useWorkspaceAccessControl';
import { actionLabelMap, subjectLabelMap } from '../../utils';

const WorkspaceAccessControlDialog = (
  props: {
    action: WorkspaceActions;
    subject: WorkspaceSubjects;
    actionVerb?: string;
    className?: string;
    resource?: string;
    fallback?: React.ReactNode;
  } & { children: React.ReactNode },
) => {
  const { action, subject, actionVerb, children, fallback, resource, className } = props;
  const [showDialog, { on, off }] = useToggle();
  const actionLabel = actionVerb ?? actionLabelMap[action];
  const resourceLabel = resource ?? subjectLabelMap[subject];

  const { canAccess } = useWorkspaceAccessControl(action, subject);

  const openDialog = React.useCallback<React.MouseEventHandler<HTMLDivElement>>(
    (e) => {
      e.stopPropagation();
      on();
    },
    [on],
  );

  if (canAccess) {
    return <>{children}</>;
  }

  return (
    <>
      <Box className={cn('cursor-pointer', className)} onClickCapture={openDialog}>
        {fallback ? fallback : children}
      </Box>
      {showDialog ? (
        <MissingPermissionDialog
          actionVerb={toLower(actionLabel)}
          resource={toLower(resourceLabel)}
          onClose={off}
        />
      ) : null}
    </>
  );
};

export default React.memo(WorkspaceAccessControlDialog);
